// One shared WebSocket per Binance futures stream path; streams are added and removed on the fly
// (SUBSCRIBE / UNSUBSCRIBE), so the whole page needs two connections instead of one per widget.

// Binance split the futures streams: depth lives on /public, everything else on /market
export const BINANCE_STREAM_HOST = {
  MARKET: "wss://fstream.binance.com/market/stream",
  PUBLIC: "wss://fstream.binance.com/public/stream",
};

export const STREAM_STATUS = {
  CONNECTING: "connecting",
  LIVE: "live",
  DOWN: "down",
};

const STALE_AFTER_MS = 5000;
const STALE_CHECK_INTERVAL_MS = 1000;
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000];
// Switching pair unsubscribes and subscribes again right away, keep the socket for that
const IDLE_CLOSE_DELAY_MS = 5000;
// Binance accepts at most 10 incoming messages per second on one connection
const SUBSCRIPTION_BATCH_DELAY_MS = 50;

const METHOD = {
  SUBSCRIBE: "SUBSCRIBE",
  UNSUBSCRIBE: "UNSUBSCRIBE",
};

const OPPOSITE_METHOD = {
  [METHOD.SUBSCRIBE]: METHOD.UNSUBSCRIBE,
  [METHOD.UNSUBSCRIBE]: METHOD.SUBSCRIBE,
};

const createConnection = (url) => {
  const entriesByStream = new Map();
  const statusListeners = new Set();
  const pending = {
    [METHOD.SUBSCRIBE]: new Set(),
    [METHOD.UNSUBSCRIBE]: new Set(),
  };

  let socket = null;
  let status = STREAM_STATUS.DOWN;
  let lastMessageAt = 0;
  let reconnectAttempt = 0;
  let requestId = 0;
  let reconnectTimer = null;
  let idleTimer = null;
  let staleTimer = null;
  let batchTimer = null;

  const setStatus = (nextStatus) => {
    if (status === nextStatus) return;
    status = nextStatus;
    statusListeners.forEach((listener) => listener(nextStatus));
  };

  const isSocketOpen = () => socket?.readyState === WebSocket.OPEN;

  const send = (method, streams) => {
    requestId += 1;
    socket.send(JSON.stringify({ method, params: streams, id: requestId }));
  };

  const clearPending = () => {
    pending[METHOD.SUBSCRIBE].clear();
    pending[METHOD.UNSUBSCRIBE].clear();
    clearTimeout(batchTimer);
    batchTimer = null;
  };

  const flushPending = () => {
    batchTimer = null;
    if (!isSocketOpen()) return;

    [METHOD.UNSUBSCRIBE, METHOD.SUBSCRIBE].forEach((method) => {
      const streams = [...pending[method]];
      pending[method].clear();
      if (streams.length) send(method, streams);
    });
  };

  const queue = (method, stream) => {
    // Subscribed and unsubscribed again before the batch went out: nothing to send
    if (pending[OPPOSITE_METHOD[method]].delete(stream)) return;

    pending[method].add(stream);
    if (!batchTimer) {
      batchTimer = setTimeout(flushPending, SUBSCRIPTION_BATCH_DELAY_MS);
    }
  };

  const stopTimers = () => {
    clearTimeout(reconnectTimer);
    clearTimeout(idleTimer);
    clearInterval(staleTimer);
    reconnectTimer = null;
    idleTimer = null;
    staleTimer = null;
  };

  const dropSocket = () => {
    const droppedSocket = socket;
    socket = null;
    clearPending();
    droppedSocket?.close();
  };

  const scheduleReconnect = () => {
    setStatus(STREAM_STATUS.DOWN);
    if (!entriesByStream.size || reconnectTimer) return;

    const delay =
      RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(connect, delay);
  };

  const handleMessage = (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    lastMessageAt = Date.now();

    // Replies to SUBSCRIBE / UNSUBSCRIBE carry an id and no stream
    if (!message?.stream) return;

    reconnectAttempt = 0;
    setStatus(STREAM_STATUS.LIVE);

    entriesByStream.get(message.stream)?.forEach((entry) => {
      try {
        entry.handler(message.data);
      } catch {
        // One broken consumer must not stop the others
      }
    });
  };

  const connect = () => {
    reconnectTimer = null;
    setStatus(STREAM_STATUS.CONNECTING);

    const nextSocket = new WebSocket(url);
    socket = nextSocket;
    lastMessageAt = Date.now();

    nextSocket.onopen = () => {
      if (socket !== nextSocket) return;
      clearPending();
      lastMessageAt = Date.now();

      const streams = [...entriesByStream.keys()];
      if (streams.length) send(METHOD.SUBSCRIBE, streams);
    };

    nextSocket.onmessage = (event) => {
      if (socket === nextSocket) handleMessage(event);
    };

    nextSocket.onerror = () => nextSocket.close();

    nextSocket.onclose = () => {
      if (socket !== nextSocket) return;
      socket = null;
      clearPending();
      scheduleReconnect();
    };
  };

  const checkStale = () => {
    if (!socket || Date.now() - lastMessageAt <= STALE_AFTER_MS) return;
    dropSocket();
    scheduleReconnect();
  };

  const ensureRunning = () => {
    clearTimeout(idleTimer);
    idleTimer = null;

    if (!socket && !reconnectTimer) connect();
    if (!staleTimer) staleTimer = setInterval(checkStale, STALE_CHECK_INTERVAL_MS);
  };

  const stop = () => {
    if (entriesByStream.size) return;
    stopTimers();
    dropSocket();
    reconnectAttempt = 0;
    setStatus(STREAM_STATUS.DOWN);
  };

  const subscribe = (stream, handler) => {
    const entry = { handler };
    let entries = entriesByStream.get(stream);

    if (!entries) {
      entries = new Set();
      entriesByStream.set(stream, entries);
      // A socket that is still connecting subscribes to every stream in onopen
      if (isSocketOpen()) queue(METHOD.SUBSCRIBE, stream);
    }

    entries.add(entry);
    ensureRunning();

    return () => {
      const currentEntries = entriesByStream.get(stream);
      if (!currentEntries?.delete(entry) || currentEntries.size) return;

      entriesByStream.delete(stream);
      if (isSocketOpen()) queue(METHOD.UNSUBSCRIBE, stream);

      if (!entriesByStream.size && !idleTimer) {
        idleTimer = setTimeout(stop, IDLE_CLOSE_DELAY_MS);
      }
    };
  };

  const watchStatus = (listener) => {
    statusListeners.add(listener);
    return () => statusListeners.delete(listener);
  };

  return {
    subscribe,
    watchStatus,
    getStatus: () => status,
  };
};

const connections = new Map();

const getConnection = (host) => {
  if (!connections.has(host)) connections.set(host, createConnection(host));
  return connections.get(host);
};

/**
 * @param {string} host one of BINANCE_STREAM_HOST
 * @param {string} stream e.g. "btcusdt@aggTrade"
 * @param {(data: object) => void} handler receives the `data` part of the combined-stream message
 * @returns {() => void} unsubscribe
 */
export const subscribeBinanceStream = (host, stream, handler) =>
  getConnection(host).subscribe(stream, handler);

/**
 * Watching does not open a connection.
 * @param {string} host one of BINANCE_STREAM_HOST
 * @param {(status: string) => void} listener called with one of STREAM_STATUS
 * @returns {() => void} stop watching
 */
export const watchBinanceStreamStatus = (host, listener) =>
  getConnection(host).watchStatus(listener);

/** @param {string} host one of BINANCE_STREAM_HOST */
export const getBinanceStreamStatus = (host) => getConnection(host).getStatus();

/** @param {string} host one of BINANCE_STREAM_HOST */
export const isBinanceStreamLive = (host) =>
  getBinanceStreamStatus(host) === STREAM_STATUS.LIVE;
